import { Link, useNavigate } from "react-router-dom";
import {
  Sparkles, Image as ImageIcon, Palette, Download, Wand2,
  Check, Menu, X, ArrowRight, BookOpen, PenLine, Layers, FileText,
} from "lucide-react";
import { useState, useEffect, lazy, Suspense, useRef } from "react";
import { track } from "@/lib/pixels";

const LandingRest = lazy(() => import("@/pages/landing/LandingRest"));
const DemoShowcase = lazy(() => import("@/pages/landing/DemoShowcase"));

/* ---------- Sticky premium header ---------- */
function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-[#080812]/85 border-b border-white/[0.06]" : "bg-transparent"
      }`}>
      <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2.5 group">
          <div className="relative w-9 h-9 rounded-xl bg-gradient-to-br from-[#7C5CFF] via-[#8B5CF6] to-[#FF4FD8] flex items-center justify-center shadow-[0_10px_24px_-6px_rgba(255,79,216,0.5)]">
            <span className="text-white font-black text-sm">B</span>
          </div>
          <span className="font-heading font-bold text-[18px] tracking-tight text-white">
            BookForge <span className="text-slate-500 font-medium">AI</span>
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-2.5">
          <Link data-testid="nav-shop-link" to="/shop"
                className="text-[13px] text-slate-300 hover:text-white px-3 py-1.5 transition-colors">
            Shop
          </Link>
          <Link data-testid="nav-login-link" to="/login"
                className="text-[13px] text-slate-300 hover:text-white px-3 py-1.5 transition-colors">
            Accedi
          </Link>
          <Link data-testid="nav-register-link" to="/register"
                className="relative inline-flex items-center gap-1.5 text-[13px] font-semibold text-white px-4 py-2 rounded-full bg-gradient-to-r from-[#7C5CFF] via-[#FF4FD8] to-[#FF7A45] hover:brightness-110 shadow-[0_10px_26px_-6px_rgba(255,79,216,0.55)] transition-all hover:-translate-y-[1px]">
            Inizia gratis <ArrowRight className="w-3.5 h-3.5"/>
          </Link>
        </div>

        <button data-testid="mobile-menu-toggle" onClick={() => setOpen(v => !v)}
                className="md:hidden text-slate-200 p-2 -mr-2" aria-label="Menu">
          {open ? <X className="w-5 h-5"/> : <Menu className="w-5 h-5"/>}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-[#080812]/95 backdrop-blur-xl border-t border-white/[0.06]">
          <div className="px-5 py-4 flex flex-col gap-3">
            <Link to="/shop" data-testid="mobile-nav-shop" className="text-sm text-slate-300 py-1.5">Shop</Link>
            <Link to="/login" data-testid="mobile-nav-login" className="text-sm text-slate-300 py-1.5">Accedi</Link>
            <Link to="/register" data-testid="mobile-nav-register"
                  className="mt-1 text-center text-sm font-semibold text-white py-3 rounded-full bg-gradient-to-r from-[#7C5CFF] via-[#FF4FD8] to-[#FF7A45]">
              Inizia gratis
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

/* ---------- Hero orbit scene (5 mascots + floating badges) ---------- */
const MASCOTS = [
  { src: "/mascot/forge_writer_tr.png", role: "Writer",   pos: "top-[6%]  left-[4%]",   size: "w-24 md:w-32",  tilt: "-rotate-3",  delay: "0s"   },
  { src: "/mascot/forge_image_tr.png",  role: "Images",   pos: "top-[2%]  right-[4%]",  size: "w-24 md:w-32",  tilt: "rotate-3",   delay: "0.4s" },
  { src: "/mascot/forge_hero_v2_tr.png",role: "Hero",     pos: "top-[22%] left-1/2 -translate-x-1/2", size: "w-56 md:w-80", tilt: "", delay: "0.2s" },
  { src: "/mascot/forge_cover_tr.png",  role: "Cover",    pos: "bottom-[6%] left-[2%]", size: "w-24 md:w-32",  tilt: "rotate-2",   delay: "0.8s" },
  { src: "/mascot/forge_export_tr.png", role: "Export",   pos: "bottom-[6%] right-[2%]",size: "w-24 md:w-32",  tilt: "-rotate-2",  delay: "1.2s" },
];

const HERO_BADGES = [
  { label: "Idea",     icon: Sparkles,  color: "from-[#7C5CFF] to-[#8B5CF6]",  pos: "top-[14%] left-1/2 -translate-x-[160%]" },
  { label: "Capitoli", icon: PenLine,   color: "from-[#8B5CF6] to-[#C084FC]",  pos: "top-[32%] left-1/2 translate-x-[110%]" },
  { label: "Immagini", icon: ImageIcon, color: "from-[#C084FC] to-[#FF4FD8]",  pos: "top-[52%] left-1/2 -translate-x-[160%]" },
  { label: "Copertina",icon: Palette,   color: "from-[#FF4FD8] to-[#FF6B6B]",  pos: "top-[62%] left-1/2 translate-x-[110%]" },
  { label: "PDF/EPUB", icon: Download,  color: "from-[#FF6B6B] to-[#FF7A45]",  pos: "bottom-[12%] left-1/2 -translate-x-1/2" },
];

function HeroBadge({ b }) {
  const Icon = b.icon;
  return (
    <div className={`absolute ${b.pos} hidden md:flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full bg-[#0D0D1A]/85 backdrop-blur-md border border-white/10 shadow-[0_10px_26px_-10px_rgba(255,79,216,0.45)] animate-float`}>
      <div className={`w-6 h-6 rounded-full bg-gradient-to-br ${b.color} flex items-center justify-center`}>
        <Icon className="w-3 h-3 text-white"/>
      </div>
      <span className="text-[11px] font-semibold text-white tracking-tight">{b.label}</span>
    </div>
  );
}

function Hero() {
  const nav = useNavigate();
  const onCreateFree = () => {
    try {
      track("Lead", { contents: [{
        content_id: "click_create_free_hero",
        content_type: "product",
        content_name: "Hero CTA",
      }]});
    } catch {}
    nav("/register");
  };
  return (
    <section className="relative pt-24 md:pt-28 overflow-hidden">
      {/* Ambient — warm violet→pink→orange glow */}
      <div className="pointer-events-none absolute inset-0"
           style={{background: "radial-gradient(1100px circle at 50% -8%, rgba(124,92,255,0.32), transparent 65%), radial-gradient(900px circle at 85% 25%, rgba(255,79,216,0.22), transparent 60%), radial-gradient(700px circle at 10% 70%, rgba(255,122,69,0.14), transparent 55%)"}}/>
      {/* Grid */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.3]"
           style={{
             backgroundImage: "linear-gradient(to right, rgba(148,163,184,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(148,163,184,0.08) 1px, transparent 1px)",
             backgroundSize: "56px 56px",
             maskImage: "radial-gradient(ellipse 70% 55% at 50% 10%, #000 55%, transparent 100%)",
             WebkitMaskImage: "radial-gradient(ellipse 70% 55% at 50% 10%, #000 55%, transparent 100%)",
           }}/>

      <div className="relative max-w-6xl mx-auto px-5 pt-6 md:pt-12 text-center">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.04] border border-white/10 text-[11px] text-slate-300 font-semibold tracking-wider uppercase mb-6">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-pink-400 opacity-75 animate-ping"/>
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-pink-400"/>
          </span>
          AI-powered ebook creation
        </div>

        {/* Headline — large */}
        <h1 data-testid="hero-title"
            className="font-heading text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black tracking-tight leading-[1.02] bg-gradient-to-br from-white via-[#F3E8FF] to-[#FFB4A2] bg-clip-text text-transparent max-w-5xl mx-auto">
          Crea ebook professionali
          <br className="hidden sm:inline"/>
          <span className="relative inline-block">
            <span className="bg-gradient-to-r from-[#C084FC] via-[#FF4FD8] to-[#FF7A45] bg-clip-text text-transparent">con l'AI</span>
            <span className="absolute -bottom-1 left-0 right-0 h-[8px] bg-gradient-to-r from-[#8B5CF6]/60 via-[#FF4FD8]/60 to-[#FF7A45]/60 blur-lg"/>
          </span>
        </h1>

        {/* Subtitle */}
        <p className="mt-6 text-base sm:text-lg md:text-xl text-slate-300/90 max-w-2xl mx-auto leading-relaxed">
          Da un'idea a un ebook completo con testi, immagini, copertina, PDF ed EPUB.
        </p>

        {/* CTAs */}
        <div className="mt-9 flex flex-wrap justify-center gap-3">
          <button data-testid="hero-create-ebook-button" onClick={onCreateFree}
                  className="group relative inline-flex items-center gap-2 px-7 py-4 md:px-8 md:py-4 rounded-full text-base md:text-[15px] font-bold text-white bg-gradient-to-r from-[#7C5CFF] via-[#FF4FD8] to-[#FF7A45] hover:brightness-110 shadow-[0_20px_50px_-14px_rgba(255,79,216,0.75)] transition-all hover:-translate-y-[2px]">
            <span>Prova BookForge</span>
            <ArrowRight className="w-4 h-4 md:w-5 md:h-5 transition-transform group-hover:translate-x-0.5"/>
            <span className="absolute inset-0 rounded-full ring-1 ring-white/20 pointer-events-none"/>
          </button>
          <Link to="/shop" data-testid="hero-shop-button"
                className="group relative inline-flex items-center gap-2 px-7 py-4 rounded-full text-base md:text-[15px] font-bold text-white bg-[#0D0D1A]/80 border border-white/15 backdrop-blur-md hover:border-pink-400/60 hover:bg-[#15152A]/90 shadow-[0_14px_34px_-14px_rgba(124,92,255,0.55)] transition-all hover:-translate-y-[2px]">
            <span className="absolute inset-0 rounded-full bg-gradient-to-r from-[#7C5CFF]/15 via-[#FF4FD8]/15 to-[#FF7A45]/15 pointer-events-none"/>
            <span className="relative">Shop Ebook</span>
            <ArrowRight className="relative w-4 h-4 md:w-5 md:h-5 transition-transform group-hover:translate-x-0.5"/>
          </Link>
          <a href="#demo" data-testid="hero-how-btn"
             className="inline-flex items-center gap-2 px-6 py-4 rounded-full text-[15px] font-semibold text-slate-200 bg-white/[0.04] border border-white/10 backdrop-blur-md hover:border-pink-400/50 hover:bg-white/[0.08] transition-all">
            Guarda demo
          </a>
        </div>

        <div className="mt-5 flex justify-center items-center gap-2 text-xs text-slate-400">
          <Check className="w-3.5 h-3.5 text-pink-400"/> Nessuna carta richiesta
        </div>
      </div>

      {/* ---- 3D scene ---- */}
      <div className="relative mt-10 md:mt-14 max-w-6xl mx-auto px-5">
        <div className="relative mx-auto w-full max-w-[1000px] aspect-[16/11] md:aspect-[16/9]">
          {/* Soft orbital glow */}
          <div className="absolute inset-10 md:inset-20 rounded-full"
               style={{background: "radial-gradient(circle, rgba(255,79,216,0.22) 0%, rgba(124,92,255,0.18) 40%, transparent 70%)", filter: "blur(40px)"}}/>
          {/* Pedestal */}
          <div className="absolute bottom-[6%] left-1/2 -translate-x-1/2 w-[55%] h-10 rounded-[50%]"
               style={{background: "radial-gradient(ellipse at center, rgba(255,79,216,0.5), transparent 70%)", filter: "blur(18px)"}}/>

          {/* Mascots */}
          {MASCOTS.map((m, i) => (
            <img key={i}
                 data-testid={`hero-mascot-${m.role.toLowerCase()}`}
                 src={m.src}
                 alt={`Forge ${m.role}`}
                 loading={i === 2 ? "eager" : "lazy"}
                 fetchpriority={i === 2 ? "high" : "auto"}
                 decoding="async"
                 className={`absolute ${m.pos} ${m.size} ${m.tilt} object-contain select-none drop-shadow-[0_30px_30px_rgba(255,79,216,0.3)] animate-float-slow`}
                 style={{animationDelay: m.delay}}
            />
          ))}

          {/* Floating pipeline badges (desktop) */}
          {HERO_BADGES.map((b, i) => <HeroBadge key={i} b={b}/>)}
        </div>

        {/* Mobile pipeline strip */}
        <div className="md:hidden mt-6 flex items-center justify-center gap-1.5 flex-wrap">
          {HERO_BADGES.map((b, i) => {
            const Icon = b.icon;
            return (
              <div key={i} className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/[0.04] border border-white/10">
                <div className={`w-5 h-5 rounded-full bg-gradient-to-br ${b.color} flex items-center justify-center`}>
                  <Icon className="w-2.5 h-2.5 text-white"/>
                </div>
                <span className="text-[10px] font-semibold text-white">{b.label}</span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------- Main Landing ---------- */
export default function Landing() {
  const [showRest, setShowRest] = useState(false);
  useEffect(() => {
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 300));
    const h = idle(() => setShowRest(true));
    try {
      track("ViewContent", {
        contents: [{ content_id: "landing", content_type: "product", content_name: "BookForge Landing" }],
      });
    } catch {}
    return () => { if (window.cancelIdleCallback) window.cancelIdleCallback(h); };
  }, []);

  return (
    <div className="relative min-h-screen bg-[#080812] text-slate-100 font-sans antialiased overflow-x-hidden">
      {/* Noise overlay */}
      <div className="pointer-events-none fixed inset-0 opacity-[0.03] z-[1] mix-blend-overlay"
           style={{backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140'><filter id='n'><feTurbulence baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.6'/></svg>\")"}}/>

      <Header/>
      <main className="relative z-[2]">
        <Hero/>
        <Suspense fallback={<div data-testid="demo-skeleton" className="h-[500px]"/>}>
          <DemoShowcase/>
        </Suspense>
        <Suspense fallback={<div data-testid="landing-rest-skeleton" className="h-[400px]"/>}>
          {showRest && <LandingRest/>}
        </Suspense>
      </main>
    </div>
  );
}
